import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import './ContractInformationFormFields.styles.scss';
import FormFieldLabel from 'components/FormFieldLabel';
import { Field, useForm, useFormState } from 'react-final-form';
import ContractDurationField from 'components/ContractDurationField';
import {
  required,
  requiredContractDuration,
  requiredDayMonth,
} from 'validations';
import MonthDayField from 'components/MonthDayField';
import InputField from 'components/InputField';
import { useLocation } from 'react-router-dom';
import { VacancyStatus } from 'searchality-data';
import CheckboxField from 'components/CheckboxField';
import ToggleField from 'components/ToggleField';

type ContractInformationFormFieldsProps = {
  className?: string;
};

const ContractInformationFormFields: React.FC<
  ContractInformationFormFieldsProps
> = (props) => {
  const { className } = props;

  const { pathname } = useLocation();

  const { t } = useTranslation();

  const { values } = useFormState();

  const {
    status,
    isIndefiniteContract: isIndefinite,
    isProbationaryPeriod: hasProbationaryPeriod,
  } = values;

  const form = useForm();

  useEffect(() => {
    // Trigger validation when `isIndefinite` changes
    form.reset(values); // Resets form with existing values to trigger revalidation
  }, [isIndefinite, form, values]);

  const isOngoingVacancyEdit = status === VacancyStatus.ONGOING;

  const isAcademic = !pathname.includes('non-academic');

  const baseClass = 'contract-information-form-fields';
  const classes = classNames(baseClass, className);

  return (
    <div className={classes}>
      <div className={`${baseClass}__field-container`}>
        <FormFieldLabel
          text={t('SchoolProfileBuilder.CI.startingContractDuration')}
        />
        <div className={`${baseClass}__checkbox-with-label`}>
          <FormFieldLabel
            text={t('SchoolProfileBuilder.CI.isIndefiniteContract')}
          />
          <Field
            component={CheckboxField}
            name="isIndefiniteContract"
            type="checkbox"
            isStyledDefault
            disabled={isOngoingVacancyEdit}
          />
        </div>
        <Field
          name="startingContractDuration"
          component={ContractDurationField}
          validate={isIndefinite ? undefined : requiredContractDuration()}
          disabled={isOngoingVacancyEdit || isIndefinite}
        />
      </div>
      <div className={`${baseClass}__field-container`}>
        <FormFieldLabel
          text={t('SchoolProfileBuilder.CI.subsequentContractDuration')}
        />

        <Field
          name="subsequentContractDuration"
          component={ContractDurationField}
          validate={requiredContractDuration()}
          disabled={isOngoingVacancyEdit || isIndefinite}
        />
      </div>
      <div className={`${baseClass}__field-container`}>
        <FormFieldLabel
          text={t('SchoolProfileBuilder.CI.probationaryPeriod')}
        />
        <div className={`${baseClass}__probationary-field`}>
          <Field
            name="isProbationaryPeriod"
            component={ToggleField}
            validate={required()}
            noLabel="no"
            yesLabel="yes"
            disabled={isOngoingVacancyEdit}
            defaultValue={false}
          />
          {hasProbationaryPeriod && (
            <Field
              name="probationaryPeriod"
              component={ContractDurationField}
              validate={requiredContractDuration()}
              disabled={isOngoingVacancyEdit}
            />
          )}
        </div>
      </div>
      <div className={`${baseClass}__field-container`}>
        <FormFieldLabel
          text={t('SchoolProfileBuilder.CI.contractRenewalDate')}
        />
        <Field
          name="contractRenewalDate"
          component={MonthDayField}
          validate={isIndefinite ? undefined : requiredDayMonth()}
          disabled={isOngoingVacancyEdit || isIndefinite}
        />
      </div>
      {isAcademic ? (
        <div className={`${baseClass}__field-container`}>
          <FormFieldLabel
            text={t('SchoolProfileBuilder.CI.teacherContractedWorkingDays')}
          />
          <Field
            name="teacherContractedWorkingDays"
            component={InputField}
            placeholder={t('SchoolProfileBuilder.CI.eg', { days: '120' })}
            validate={required()}
            disabled={isOngoingVacancyEdit}
          />
        </div>
      ) : (
        <div className={`${baseClass}__field-container`}>
          <FormFieldLabel
            text={t('SchoolProfileBuilder.CI.adminContractedWorkingDays')}
          />
          <Field
            name="adminContractedWorkingDays"
            component={InputField}
            placeholder={t('SchoolProfileBuilder.CI.eg', { days: '50' })}
            validate={required()}
            disabled={isOngoingVacancyEdit}
          />
        </div>
      )}
      {isAcademic ? (
        <>
          <div className={`${baseClass}__field-container`}>
            <FormFieldLabel
              text={t('SchoolProfileBuilder.CI.teacherStartDate')}
            />
            <Field
              name="teacherStartDate"
              component={MonthDayField}
              validate={requiredDayMonth()}
              disabled={isOngoingVacancyEdit}
            />
          </div>
          <div className={`${baseClass}__field-container`}>
            <FormFieldLabel
              text={t('SchoolProfileBuilder.CI.teacherEndDate')}
            />
            <Field
              name="teacherEndDate"
              component={MonthDayField}
              validate={requiredDayMonth()}
              disabled={isOngoingVacancyEdit}
            />
          </div>
        </>
      ) : (
        <>
          <div className={`${baseClass}__field-container`}>
            <FormFieldLabel
              text={t('SchoolProfileBuilder.CI.adminStartDate')}
            />
            <Field
              name="adminStartDate"
              component={MonthDayField}
              validate={requiredDayMonth()}
              disabled={isOngoingVacancyEdit}
            />
          </div>
          <div className={`${baseClass}__field-container`}>
            <FormFieldLabel text={t('SchoolProfileBuilder.CI.adminEndDate')} />
            <Field
              name="adminEndDate"
              component={MonthDayField}
              validate={requiredDayMonth()}
              disabled={isOngoingVacancyEdit}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ContractInformationFormFields;
